import { AdminDesignDisputes } from "./DesignRevisions";
import { useAuth } from "../../auth/AuthContext";
import axios from "axios";
import FileDropzone from "../../components/FileDropzone";
import { useEffect, useState } from "react";
import { FileText, ShieldCheck, X, CheckCircle2, AlertTriangle } from "lucide-react";
import { API, money, abs, Modal, Empty } from "./shared";

// ============================================================
// VIEW: DISPUTES (admin)
// ============================================================
export function DisputesView() {
  const { user: authUser } = useAuth();
  const [filter, setFilter] = useState("open");
  const [disputes, setDisputes] = useState([]);
  const [selected, setSelected] = useState(null);
  const load = () => axios.get(`${API}/disputes${filter === "all" ? "" : `?status=${filter}`}`).then(r => setDisputes(r.data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter]);
  return (
    <section className="content">
      {authUser?.role === "admin" && <AdminDesignDisputes/>}
      <div className="section-heading">
        <div>
          <p className="eyebrow">DISPUTE CONTROL TOWER</p>
          <h2>Frozen escrows awaiting your verdict</h2>
          <p className="subhead">Every dispute pauses the payout. Review both parties' evidence, add investigation notes, then release funds via one of four verdicts.</p>
        </div>
      </div>
      <div className="filter-row">
        {["open", "resolved", "all"].map(f => (
          <button
            key={f}
            data-testid={`dispute-filter-${f}`}
            className={`filter ${filter === f ? "active" : ""}`}
            onClick={() => setFilter(f)}
          >
            {f[0].toUpperCase() + f.slice(1)} <b>{f === filter ? disputes.length : ""}</b>
          </button>
        ))}
      </div>
      <div className="dispute-grid">
        {disputes.length === 0 ? <Empty text="No disputes in this view."/> : disputes.map(d => (
          <button key={d.id} data-testid={`dispute-card-${d.id}`} className={`dispute-card ${d.status === "open" ? "open" : "resolved"}`} onClick={() => setSelected(d)}>
            <div className="dispute-head">
              <span className={`status disputed`}><AlertTriangle size={11}/> {d.dispute_reason}</span>
              <span className={`outcome-pill outcome-${d.status === "resolved" ? "won" : "submitted"}`}>{d.status}</span>
            </div>
            <h3>{d._job?.title || d.job_id}</h3>
            <p>Raised by {d._raised_by?.name} · <em>{d._raised_by?.role}</em></p>
            <div className="dispute-meta">
              <span>Gross {money(d._job?.awarded_price)}</span>
              <span>{new Date(d.created_at).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" })}</span>
            </div>
            {d.resolution_verdict && <div className="verdict-tag">Verdict: {d.resolution_verdict}</div>}
          </button>
        ))}
      </div>
      {selected && <DisputeDetailDrawer dispute={selected} close={() => setSelected(null)} refresh={load}/>}
    </section>
  );
}

export function DisputeDetailDrawer({ dispute, close, refresh }) {
  const [d, setD] = useState(dispute);
  const [note, setNote] = useState("");
  const [showResolve, setShowResolve] = useState(false);
  const reload = async () => {
    const { data } = await axios.get(`${API}/disputes/${dispute.id}`);
    setD(data);
  };
  useEffect(() => { reload().catch(() => {}); }, [dispute.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const addNote = async () => {
    if (!note.trim()) return;
    await axios.patch(`${API}/disputes/${d.id}/notes?note=${encodeURIComponent(note)}`);
    setNote("");
    await reload();
  };
  return (
    <div className="drawer-backdrop" onClick={close}>
      <aside className="drawer" onClick={e => e.stopPropagation()}>
        <button data-testid="dispute-drawer-close" className="close-btn" onClick={close}><X size={18}/></button>
        <div className="drawer-top">
          <p className="eyebrow">DISPUTE · {d.id}</p>
          <h2>{d._job?.title || d.job_id}</h2>
          <div className="drawer-status-row">
            <span className="status disputed"><AlertTriangle size={11}/> {d.dispute_reason}</span>
            <span className={`outcome-pill outcome-${d.status === "resolved" ? "won" : "submitted"}`}>{d.status}</span>
          </div>
        </div>

        <div className="detail-block">
          <label>Raised by</label><b>{d._raised_by?.name} <em>({d._raised_by?.role})</em></b>
          {d.initial_note && <><label>Initial complaint</label><p>{d.initial_note}</p></>}
          {d._payment && <>
            <label>Payment status</label>
            <b><span className={d._payment.payment_status === "Frozen" ? "pay-frozen" : d._payment.payment_status === "Released" ? "pay-released" : "pay-held"}>{d._payment.payment_status}</span> · {money(d._payment.gross)}</b>
          </>}
        </div>

        <EvidenceBlock title="Buyer evidence" urls={d.buyer_evidence_urls} testid="buyer-evidence"/>
        <EvidenceBlock title="Printer evidence" urls={d.printer_evidence_urls} testid="printer-evidence"/>

        <div className="drawer-section-title" style={{ marginTop: 22 }}>
          <h3>Admin investigation notes</h3>
          <span>Appended with timestamps</span>
        </div>
        {d.admin_notes ? <pre className="admin-notes" data-testid="admin-notes">{d.admin_notes}</pre> : <p className="muted">No notes yet.</p>}
        {d.status === "open" && (
          <div className="note-input">
            <textarea data-testid="admin-note-input" placeholder="Add investigation note…" value={note} onChange={e => setNote(e.target.value)}/>
            <button data-testid="admin-note-add" className="primary-btn" onClick={addNote}>Append note</button>
          </div>
        )}

        {d.status === "open" && (
          <button data-testid="open-resolve-button" className="primary-btn full" onClick={() => setShowResolve(true)}>
            Resolve dispute <ShieldCheck size={16}/>
          </button>
        )}
        {d.status === "resolved" && d.resolution_outcome && (
          <div className="drawer-callout ok" data-testid="resolution-summary">
            <CheckCircle2 size={17}/>
            <div>
              <b>Resolved — {d.resolution_verdict}</b>
              <span>
                {d.resolution_outcome.buyer_refund ? `Buyer refund: ${money(d.resolution_outcome.buyer_refund)} · ` : ""}
                {d.resolution_outcome.supplier_payout ? `Supplier payout: ${money(d.resolution_outcome.supplier_payout)} · ` : ""}
                {d.resolution_outcome.platform_commission ? `Commission: ${money(d.resolution_outcome.platform_commission)}` : ""}
              </span>
            </div>
          </div>
        )}
        {showResolve && (
          <ResolveDisputeModal
            dispute={d}
            close={() => setShowResolve(false)}
            refresh={async () => { await reload(); refresh(); }}
          />
        )}
      </aside>
    </div>
  );
}

export function EvidenceBlock({ title, urls, testid }) {
  return (
    <div className="evidence-block" data-testid={testid}>
      <label>{title}</label>
      {urls?.length ? (
        <div className="evidence-list">
          {urls.map((u, i) => {
            const full = abs(u);
            const isImg = /\.(png|jpe?g|gif|webp|heic|avif|bmp|svg)(\?|$)/i.test(u) || /(unsplash|imgur|cloudinary|githubusercontent|images)/i.test(u) || /\/api\/files\//.test(u);
            return (
              <a key={i} href={full} target="_blank" rel="noreferrer" className="evidence-item" data-testid={`${testid}-${i}`}>
                {isImg
                  ? <img src={full} alt="evidence" onError={(e) => { e.currentTarget.style.display = "none"; }}/>
                  : <div className="evidence-file"><FileText size={16}/> <span>{u.split("/").pop() || u}</span></div>}
              </a>
            );
          })}
        </div>
      ) : <p className="muted">No evidence submitted.</p>}
    </div>
  );
}

// ============================================================
// MODAL: Raise a dispute (buyer or printer)
// ============================================================
export function DisputeModal({ job, role, supplierId, close, refresh }) {
  const raisedBy = role === "buyer" ? "buyer-1" : supplierId;
  const [reason, setReason] = useState("Quality Defect");
  const [evidenceFiles, setEvidenceFiles] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await axios.post(`${API}/jobs/${job.id}/disputes`, {
        raised_by: raisedBy,
        dispute_reason: reason,
        evidence_urls: [],
        evidence_file_ids: evidenceFiles.map(x => x.id),
        note: noteText,
      });
      await refresh();
      close();
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Failed to raise dispute");
    }
  };
  return (
    <Modal title="Raise a dispute" close={close}>
      <div className="drawer-callout danger">
        <AlertTriangle size={17}/>
        <div>
          <b>This will freeze the escrow.</b>
          <span>Funds will remain locked until an admin resolves the case. Both parties can upload evidence.</span>
        </div>
      </div>
      <form onSubmit={submit} className="form-grid">
        <label>Reason
          <select data-testid="dispute-reason-select" value={reason} onChange={e => setReason(e.target.value)}>
            <option>Quality Defect</option>
            <option>Late Delivery</option>
            <option>Spec Mismatch</option>
            <option>Artwork Error</option>
          </select>
        </label>
        <label>Job
          <input value={job.title} disabled/>
        </label>
        <label style={{ gridColumn: "span 2" }}>Describe what went wrong
          <textarea data-testid="dispute-note-input" value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Boxes arrived crushed, colour is off, arrived 5 days late…" required/>
        </label>
        <div className="modal-full-row">
          <label className="dz-label">Evidence — drop photos or short videos</label>
          <FileDropzone
            testid="dispute-evidence-dropzone"
            scope="evidence"
            accept="image/*,video/*,application/pdf"
            value={evidenceFiles}
            onChange={setEvidenceFiles}
            label="Drop photos or short videos"
            hint="JPG · PNG · WebP · MP4 — up to 60 MB each"
          />
        </div>
        {err && <div className="form-error" data-testid="dispute-error">{err}</div>}
        <div className="form-actions">
          <button data-testid="dispute-cancel" type="button" className="secondary-btn" onClick={close}>Cancel</button>
          <button data-testid="dispute-submit" className="primary-btn">Freeze escrow & open case <AlertTriangle size={15}/></button>
        </div>
      </form>
    </Modal>
  );
}

// ============================================================
// MODAL: Admin resolves dispute
// ============================================================
export function ResolveDisputeModal({ dispute, close, refresh }) {
  const [verdict, setVerdict] = useState("Refund Buyer");
  const [split, setSplit] = useState(50);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    await axios.post(`${API}/disputes/${dispute.id}/resolve`, {
      verdict,
      admin_notes: notes,
      split_pct_to_buyer: split,
    });
    await refresh();
    setBusy(false);
    close();
  };
  const gross = dispute._job?.awarded_price || dispute._payment?.gross || 0;
  const buyerShare = verdict === "Refund Buyer" ? gross : verdict === "Partial Split" ? (gross * split / 100) : 0;
  const supplierGross = verdict === "Pay Printer" ? gross : verdict === "Partial Split" ? (gross - buyerShare) : 0;
  const sharePct = verdict === "Pay Printer" ? 100 : verdict === "Partial Split" ? 100 - split : 0;
  const [calc, setCalc] = useState(null);
  const jobId = dispute._job?.id || dispute.job_id;
  useEffect(() => {
    if (!jobId || !sharePct) { setCalc(null); return; }
    axios.get(`${API}/admin/jobs/${jobId}/payout-preview`, { params: { share_pct: sharePct } }).then(r => setCalc(r.data)).catch(() => setCalc(null));
  }, [jobId, sharePct]);
  const commission = calc?.commission_inr || 0;
  const supplierPayout = calc?.payout_inr || 0;
  return (
    <Modal title="Resolve dispute" close={close}>
      <div className="verdict-grid">
        {[
          { v: "Refund Buyer", d: "Full refund. Escrow released back to buyer." },
          { v: "Pay Printer", d: "Full payout to printer minus platform commission." },
          { v: "Reprint", d: "Printer redoes the run. Escrow stays Frozen." },
          { v: "Partial Split", d: "Split funds between buyer & printer." },
        ].map(o => (
          <button
            key={o.v}
            type="button"
            data-testid={`verdict-${o.v.replace(/\s+/g,"-").toLowerCase()}`}
            className={`verdict-card ${verdict === o.v ? "active" : ""}`}
            onClick={() => setVerdict(o.v)}
          >
            <b>{o.v}</b>
            <span>{o.d}</span>
          </button>
        ))}
      </div>
      {verdict === "Partial Split" && (
        <div className="split-slider">
          <label>Buyer receives <b>{split}%</b> · Printer receives <b>{100 - split}%</b></label>
          <input data-testid="split-slider" type="range" min="0" max="100" step="5" value={split} onChange={e => setSplit(Number(e.target.value))}/>
        </div>
      )}
      <div className="verdict-preview" data-testid="verdict-preview">
        <div><span>Buyer refund</span><b>{money(buyerShare)}</b></div>
        <div><span>Supplier payout</span><b>{money(supplierPayout)}</b></div>
        <div><span>Platform commission</span><b>{money(commission)}</b></div>
        {calc && sharePct > 0 && <div data-testid="verdict-preview-breakdown" style={{gridColumn:"1 / -1",fontSize:11,color:"#64748b"}}>= {money(calc.supplier_taxable_inr)} + {money(calc.supplier_gst_inr)} GST @{calc.gst_rate_pct}% − {money(calc.commission_inr)} platform fee ({calc.take_rate_pct}%) − {money(calc.platform_fee_gst_inr)} GST on fee − {money(calc.tds_inr)} TDS 1%</div>}
      </div>
      <label className="proof-feedback">Investigation summary
        <textarea data-testid="verdict-notes" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Case findings, next steps, audit trail…"/>
      </label>
      <div className="form-actions">
        <button data-testid="verdict-cancel" className="secondary-btn" onClick={close}>Cancel</button>
        <button data-testid="verdict-submit" className="primary-btn" disabled={busy} onClick={submit}>{busy ? "Releasing funds…" : "Confirm verdict & execute"} <ShieldCheck size={15}/></button>
      </div>
    </Modal>
  );
}
